// @ts-nocheck
// Location: src/components/shared/SwarmCommentsStatus.tsx
// Purpose: Swarm comments status component for showing the status of a swarm comments pod
// Scope: Shows the status of a swarm comments pod and allows for resetting and running the pod

/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Id } from '@/convex/_generated/dataModel';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useSelectedPodStore } from '@/stores/selectedPodStore';
import { Doc } from '@/convex/_generated/dataModel';

// Add type definition for scheduled comments
interface ScheduledComment {
	profileKey: string;
	status?: string;
	scheduledFor?: string;
}

interface SwarmCommentsStatusProps {
	contentHistoryId: Id<'contentHistory'>;
	podId: Id<'engagementPods'>;
	className?: string;
}

function TurboModeDialog() {
	const [open, setOpen] = React.useState(false);

	return (
		<>
			<Badge
				variant='outline'
				className='rounded-full px-1 py-0 border-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer'
				onClick={() => setOpen(true)}
			>
				?
			</Badge>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className='sm:max-w-md'>
					<DialogHeader>
						<div className='flex flex-col items-center justify-center'>
							<img
								src='/logo.png'
								alt='Prometheus'
								className='h-12 mb-4'
							/>
							<DialogTitle className='text-center text-xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 text-transparent bg-clip-text'>
								⚡️ Turbo Mode ⚡️
							</DialogTitle>
							<DialogDescription className='text-center mt-4'>
								<div className='mb-4'>
									In Turbo Mode, your SWARM Pod operates at
									maximum efficiency:
								</div>
								<div className='space-y-4 text-left mt-4'>
									<div className='flex items-start gap-2'>
										<span className='text-xl'>🚀</span>
										<div>
											Comments are posted back-to-back
											with just a 3-second cooldown
										</div>
									</div>
									<div className='flex items-start gap-2'>
										<span className='text-xl'>⏰</span>
										<div>
											No more random 2-10 minute delays
											between comments
										</div>
									</div>
									<div className='flex items-start gap-2'>
										<span className='text-xl'>🔥</span>
										<div>
											Perfect for time-sensitive content
											that needs immediate engagement
										</div>
									</div>
								</div>
								<div className='mt-6 p-4 bg-pink-500/10 rounded-lg'>
									<div className='text-sm text-pink-600 dark:text-pink-400'>
										Use with caution! Turbo Mode is like
										giving your Pod a shot of espresso -
										it'll work faster but might attract more
										attention!
									</div>
								</div>
							</DialogDescription>
						</div>
					</DialogHeader>
				</DialogContent>
			</Dialog>
		</>
	);
}

export function SwarmCommentsStatus({
	contentHistoryId,
	podId,
	className,
}: SwarmCommentsStatusProps) {
	const [showDetails, setShowDetails] = React.useState(false);
	const [isRunning, setIsRunning] = React.useState(false);
	const [isResetting, setIsResetting] = React.useState(false);
	const [turboMode, setTurboMode] = React.useState(false);
	const navigate = useNavigate();
	const { user } = useAuth();
	const { setSelectedPod } = useSelectedPodStore();

	// Get pod details to show member information
	const pod = useQuery(api.engagementPods.get, { podId });

	// Get scheduled comments for this content
	const scheduledComments =
		useQuery(api.commentQueue.getScheduledComments, {
			contentHistoryId,
			podId,
		}) ?? [];

	// Get published content
	const publishedContent = useQuery(
		api.publishing.internalGetPublishedContentByHistoryId,
		{
			contentHistoryId,
		}
	);

	// Get the schedule for this pod
	const schedules = useQuery(api.schedules.getByTargetId, {
		targetId: podId,
	});
	const schedule = schedules?.[0];

	// Track if the task is actually running from the schedule state
	const isTaskRunning = schedule?.isRunning || false;

	// Combine local and schedule running states
	const isButtonDisabled = isRunning || isTaskRunning || isResetting;
	const isResetDisabled = isResetting;
	const buttonText = isTaskRunning ? (
		'Task in progress'
	) : isRunning ? (
		'Starting...'
	) : (
		<>
			<span className='mr-2'>🚀</span>
			Run Pod Now
		</>
	);

	// Mutations and actions
	const runSwarmComments = useMutation(api.tasks.swarmComments.run);
	const resetSwarmComments = useMutation(
		api.twitterXComments.clearScheduledComments
	);
	const createSchedule = useMutation(api.schedules.create);
	const addUrl = useMutation(api.urls.addUrl);

	const handleRunNow = async () => {
		if (!pod) {
			toast({
				title: 'Error',
				description:
					'Pod not found. Please try again or contact support.',
				variant: 'destructive',
				duration: 3000,
			});
			return;
		}

		if (!publishedContent?.postUrl) {
			toast({
				title: 'Error',
				description:
					'Content must be published before running SWARM comments.',
				variant: 'destructive',
				duration: 3000,
			});
			return;
		}

		if (!user?.id) {
			toast({
				title: 'Error',
				description: 'You must be logged in to run SWARM comments.',
				variant: 'destructive',
				duration: 3000,
			});
			return;
		}

		if (pod.mode !== 'swarm') {
			toast({
				title: 'Error',
				description: 'This pod must be in SWARM mode to run comments.',
				variant: 'destructive',
				duration: 3000,
			});
			return;
		}

		setIsRunning(true);
		try {
			// Create schedule if it doesn't exist
			let scheduleId = schedule?._id;
			if (!scheduleId) {
				// Create URL for the pod first
				await addUrl({
					url: publishedContent.postUrl,
					platform: pod.platform,
					podId,
					conversationId: publishedContent.conversationId,
					isActive: true,
					mode: 'swarm',
					userId: user.id as unknown as Id<'users'>,
					username:
						publishedContent.postUrl.split('/').pop() || 'unknown',
					profileKey: null,
				});

				console.log('Creating new schedule for pod:', podId);
				scheduleId = await createSchedule({
					name: 'SWARM Comments',
					description: 'Automated SWARM comments schedule',
					type: 'swarm',
					targetId: podId,
					schedule: {
						frequency: 'daily',
						times: ['00:00'],
						timezone:
							Intl.DateTimeFormat().resolvedOptions().timeZone,
					},
					metadata: {
						urlCount: 1,
						commentsPerRun: 'ALL',
						actionCount: 'ALL',
						mode: 'swarm',
					},
				});
				console.log('Created new schedule:', scheduleId);
			}

			console.log('Running swarm comments with:', {
				scheduleId,
				podId,
				isManualRun: true,
				turboMode,
			});

			await runSwarmComments({
				scheduleId,
				podId,
				isManualRun: true,
				turboMode,
			});

			toast({
				title: '🐝 SWARM Started',
				description: turboMode
					? 'Pod is running in TURBO mode! ⚡️'
					: 'Pod is running normally...',
				duration: 3000,
			});
		} catch (error) {
			console.error('Failed to run swarm comments:', error);
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to run SWARM comments',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsRunning(false);
		}
	};

	const handleReset = async () => {
		console.log('🔴 Reset button clicked', {
			contentHistoryId,
			podId,
		});
		setIsResetting(true);
		try {
			toast({
				title: '🔄 Resetting Pod',
				description: 'Cancelling scheduled comments...',
				duration: 3000,
			});
			console.log('🟡 About to call clearScheduledComments');
			const result = await resetSwarmComments({
				contentHistoryId,
				podId,
			});
			console.log('🟢 clearScheduledComments returned:', result);
			toast({
				title: '✅ Pod Reset Complete',
				description: `Cancelled ${result} scheduled comments`,
				duration: 3000,
			});
		} catch (error) {
			console.error('❌ Reset failed:', {
				error: error instanceof Error ? error.message : 'Unknown error',
				contentHistoryId,
				podId,
			});
			toast({
				title: '❌ Reset Failed',
				description:
					error instanceof Error
						? error.message
						: 'Failed to reset pod',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsResetting(false);
		}
	};

	// Check if there are any pending or processing comments
	const hasPendingComments = scheduledComments.some(
		(comment: ScheduledComment) =>
			comment.status === 'pending' || comment.status === 'processing'
	);

	const summaryStats = {
		completed: scheduledComments.filter(
			(c: ScheduledComment) => c.status === 'completed'
		).length,
		pending: scheduledComments.filter(
			(c: ScheduledComment) => c.status === 'pending'
		).length,
		processing: scheduledComments.filter(
			(c: ScheduledComment) => c.status === 'processing'
		).length,
		failed: scheduledComments.filter(
			(c: ScheduledComment) => c.status === 'error'
		).length,
	};

	if (!pod) return null;

	return (
		<>
			<div className={cn('mt-4 mb-8 space-y-4', className)}>
				<div className='flex items-center gap-4 border-t border-gray-800 pt-8'>
					<motion.button
						onClick={() => setShowDetails(!showDetails)}
						className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
						animate={{ rotate: showDetails ? 180 : 0 }}
						transition={{ duration: 0.2 }}
					>
						{showDetails ? '🔽' : '▶️'}
					</motion.button>
					<h3 className='text-lg font-semibold'>
						<span>🐝</span>{' '}
						<span className='bg-gradient-to-r from-pink-400 to-pink-600 text-transparent bg-clip-text'>
							SWARM Pod Status
						</span>
					</h3>
					<Badge
						variant='outline'
						className='bg-gradient-to-b from-purple-300 via-purple-500 to-purple-800 text-white border-0'
					>
						{pod.members.length} Members
					</Badge>
					<div className='flex flex-col gap-2 ml-auto'>
						<div className='flex gap-2'>
							<Button
								size='sm'
								variant='outline'
								// Set selected pod in store before navigation to ensure
								// PodExpandedView will show this specific pod when page loads
								onClick={() => {
									setSelectedPod(podId);
									navigate('/admin/pods');
								}}
								className='bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 border-0'
							>
								<>
									<span className='mr-2'>✏️</span>
									Edit Pod
								</>
							</Button>
							<Button
								size='sm'
								variant='outline'
								onClick={handleReset}
								disabled={isResetDisabled}
								className='bg-gradient-to-r from-red-500 to-orange-600 text-white hover:from-red-600 hover:to-orange-700 border-0'
							>
								{isResetting ? (
									<>
										<Loader2 className='mr-2 h-4 w-4 animate-spin' />
										Resetting...
									</>
								) : (
									<>
										<span className='mr-2'>🔄</span>
										Reset Pod
									</>
								)}
							</Button>
							<div className='flex flex-col items-center gap-2'>
								<Button
									size='sm'
									variant='outline'
									onClick={handleRunNow}
									disabled={isButtonDisabled}
									className='bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700 border-0'
								>
									{(isRunning || isTaskRunning) && (
										<Loader2 className='mr-2 h-4 w-4 animate-spin' />
									)}
									{buttonText}
								</Button>
								<div className='flex items-center space-x-2 mt-2'>
									<div className='flex items-center gap-2'>
										<Label
											htmlFor='turbo-mode'
											className={cn(
												'text-sm font-medium cursor-pointer select-none transition-colors',
												turboMode
													? 'text-pink-500'
													: 'text-gray-400'
											)}
										>
											{turboMode && <span>⚡️ </span>}
											Turbo
										</Label>
									</div>
									<Switch
										id='turbo-mode'
										checked={turboMode}
										onCheckedChange={setTurboMode}
										className='data-[state=checked]:bg-pink-500'
									/>
									<TurboModeDialog />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<AnimatePresence>
				{showDetails && (
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: 'auto' }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.2 }}
					>
						<Card className='p-6 space-y-4'>
							{/* Pod Members and Their Comment Status */}
							<div className='h-[300px] overflow-y-auto pr-2 space-y-3 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent'>
								{pod.members.map(
									(
										member: Doc<'engagementPods'>['members'][number],
										index: number
									) => {
										const memberComment =
											scheduledComments.find(
												(comment: ScheduledComment) =>
													comment.profileKey ===
													member.socialUsername
											);

										return (
											<div
												key={member.email}
												className='flex items-center justify-between gap-4 p-3 rounded-lg bg-gray-100 dark:bg-gray-800'
											>
												<div className='flex items-center gap-3'>
													<Badge
														className={cn(
															'px-2 py-1',
															memberComment
																? memberComment.status ===
																	'completed'
																	? 'bg-gradient-to-b from-green-400 to-green-600'
																	: memberComment.status ===
																		  'pending'
																		? 'bg-gradient-to-b from-yellow-400 to-yellow-600'
																		: memberComment.status ===
																			  'error'
																			? 'bg-gradient-to-b from-red-400 to-red-600'
																			: 'bg-gradient-to-b from-blue-400 to-blue-600'
																: 'bg-gradient-to-b from-gray-400 to-gray-600'
														)}
													>
														{memberComment
															? memberComment.status ===
																'completed'
																? '✅'
																: memberComment.status ===
																	  'pending'
																	? '⏳'
																	: memberComment.status ===
																		  'error'
																		? '❌'
																		: '🔄'
															: '⏰'}
													</Badge>
													<span className='font-medium'>
														{member.email}
													</span>
												</div>
												<div className='text-sm text-gray-500'>
													{memberComment ? (
														memberComment.scheduledFor ? (
															<span>
																Scheduled for{' '}
																{new Date(
																	memberComment.scheduledFor
																).toLocaleTimeString()}{' '}
																(
																{formatDistanceToNow(
																	new Date(
																		memberComment.scheduledFor
																	),
																	{
																		addSuffix:
																			true,
																	}
																)}
																)
															</span>
														) : (
															'Processing...'
														)
													) : (
														'Awaiting schedule'
													)}
												</div>
											</div>
										);
									}
								)}
							</div>

							{/* Summary Stats */}
							<div className='grid grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700'>
								<div className='text-center'>
									<div className='text-2xl font-bold text-green-500'>
										{summaryStats.completed}
									</div>
									<div className='text-sm text-gray-500'>
										Completed
									</div>
								</div>
								<div className='text-center'>
									<div className='text-2xl font-bold text-yellow-500'>
										{summaryStats.pending}
									</div>
									<div className='text-sm text-gray-500'>
										Pending
									</div>
								</div>
								<div className='text-center'>
									<div className='text-2xl font-bold text-blue-500'>
										{summaryStats.processing}
									</div>
									<div className='text-sm text-gray-500'>
										Processing
									</div>
								</div>
								<div className='text-center'>
									<div className='text-2xl font-bold text-red-500'>
										{summaryStats.failed}
									</div>
									<div className='text-sm text-gray-500'>
										Failed
									</div>
								</div>
							</div>
						</Card>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
}
